import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import {
  symbolData,
  BONUS_SYMBOL_IDS,
  isBonusSymbol,
  isJackpotSymbol,
} from "../../SymbolData";
import { Settings } from "../../settings";
import store from "../../State/Store/store";
import { formatToCustomLocale } from "../../utilities";

/**
 * RoyalCollectOverlaySymbolController
 *
 * Manages spine animations for a single symbol position in Royal Collect.
 * Pre-creates spines for ALL bonus symbol types (coins, jackpots, royals)
 * so any can be shown instantly via showIdleAnimation(symbolId).
 *
 * Pattern mirrors OverlayReelController's pre-positioned spine approach:
 * each spine keyed by symbolId, hidden at alpha 0.001 until needed.
 */
class RoyalCollectOverlaySymbolController {
  constructor(
    overlaySymbolContainer,
    reelIndex,
    symbolIndex,
    symbolWidth,
    symbolHeight,
  ) {
    this.overlaySymbolContainer = overlaySymbolContainer;
    this.reelIndex = reelIndex;
    this.symbolIndex = symbolIndex;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;

    // Pre-positioned spines keyed by spine name ("c", "r", "j")
    this.prePositionedSpines = new Map();
    this.activeSpineKey = null; // Currently visible spine key
    this.isAnimating = false;
    this.currentAnimationType = null;
    this.currentSymbolId = null;

    // Animation caches per spine key
    this.animationCaches = new Map(); // spineKey → Map(type → animName)
    this.animationAvailability = new Map(); // spineKey → Set(animNames)

    // Coin value text management (attached to spine slot)
    this.coinValueContainer = null;
    this.coinValueBitmapText = null;
    this._coinValueSpine = null;

    // Caches
    this._spineScaleCache = new Map();
    this._coinDisplayValueCache = new Map();

    this.initializeAllSpines();
  }

  // ── Spine Initialization ────────────────────────────────────────────

  /**
   * Pre-create spine instances for all RC bonus symbol types.
   * Only bonus symbols (coins, jackpots, royal) get overlay spines.
   * Blank symbols use static textures only — no spine overlay needed.
   * This matches the base game OverlayReelController pattern.
   */
  initializeAllSpines() {
    if (!this.overlaySymbolContainer) return;

    // Collect unique spine names from bonus symbols only (no blank).
    // Blank has spine:"blank" in symbolData but should never be rendered
    // as an overlay — it's handled entirely via static textures.
    const rcSymbolIds = [...BONUS_SYMBOL_IDS];
    const spineNames = new Map(); // spineName → representative symbolId
    for (const id of rcSymbolIds) {
      const info = symbolData[id];
      if (info?.spine && !spineNames.has(info.spine)) {
        spineNames.set(info.spine, id);
      }
    }

    for (const [spineName] of spineNames) {
      const spine = this._createSpineInstance(spineName);
      if (!spine) continue;

      const scaleData = this._calculateSpineScale(
        this.symbolWidth,
        this.symbolHeight,
      );

      spine.position.set(scaleData.x, scaleData.y);
      spine.scale.set(scaleData.scale);
      spine.zIndex = 10;
      spine.alpha = 0.001; // Hidden until needed
      spine.autoUpdate = false;

      this.overlaySymbolContainer.addChild(spine);
      this.prePositionedSpines.set(spineName, spine);

      // Build animation availability cache
      if (spine.skeleton?.data?.animations?.length > 0) {
        const available = new Set(
          spine.skeleton.data.animations.map((a) => a.name.toLowerCase()),
        );
        this.animationAvailability.set(spineName, available);
      }

      // Warm up shaders on first position only (GPU global)
      if (this.reelIndex === 0 && this.symbolIndex === 0) {
        this._warmUpAnimation(spine, spineName, "land");
        this._warmUpAnimation(spine, spineName, "idle");
        this._warmUpAnimation(spine, spineName, "appear");
        this._warmUpAnimation(spine, spineName, "win");
        this._warmUpAnimation(spine, spineName, "collect");
      }
    }
  }

  _createSpineInstance(spineName) {
    const skeletonAlias = `${spineName}-skeleton`;
    const atlasAlias = `${spineName}-atlas`;
    const assets = PIXI.Assets.cache;

    if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) return null;

    try {
      const spine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });
      spine.name = `rc_spine_${spineName}_${this.reelIndex}_${this.symbolIndex}`;
      return spine;
    } catch {
      return null;
    }
  }

  /**
   * Calculate spine scale — matches OverlayReelController exactly.
   * Uses heightScale only, x/y = 0 (spine origin is center).
   */
  _calculateSpineScale(containerWidth, containerHeight) {
    const cacheKey = `${containerWidth}_${containerHeight}`;
    if (this._spineScaleCache.has(cacheKey)) {
      return this._spineScaleCache.get(cacheKey);
    }

    const nativeHeight = Settings.SYMBOL_HEIGHT;
    const scale = containerHeight / nativeHeight;

    const result = { scale, x: 0, y: 0 };
    this._spineScaleCache.set(cacheKey, result);
    return result;
  }

  // ── Animation Selection ─────────────────────────────────────────────

  _selectAnimation(spineName, type) {
    let cache = this.animationCaches.get(spineName);
    if (!cache) {
      cache = new Map();
      this.animationCaches.set(spineName, cache);
    }

    if (cache.has(type)) return cache.get(type);

    const spine = this.prePositionedSpines.get(spineName);
    if (!spine?.skeleton?.data?.animations) return null;

    const lowerType = type.toLowerCase();
    for (const anim of spine.skeleton.data.animations) {
      if (anim.name.toLowerCase().includes(lowerType)) {
        cache.set(type, anim.name);
        return anim.name;
      }
    }

    // No exact match — for "idle", fall back to "static" (idle-equivalent for jackpot spine)
    if (type === "idle") {
      for (const anim of spine.skeleton.data.animations) {
        if (anim.name === "static") {
          cache.set(type, anim.name);
          return anim.name;
        }
      }
    }

    return null;
  }

  _hasAnimation(spineName, type) {
    const available = this.animationAvailability.get(spineName);
    if (!available) return false;
    const lowerType = type.toLowerCase();
    for (const name of available) {
      if (name.includes(lowerType)) return true;
    }
    // "static" is the idle-equivalent for spines without an explicit "idle" animation
    if (type === "idle" && available.has("static")) return true;
    return false;
  }

  _warmUpAnimation(spine, spineName, type) {
    const animName = this._selectAnimation(spineName, type);
    if (!animName || !spine?.state) return;

    try {
      spine.state.clearTracks();
      spine.state.addAnimation(0, animName, true, 0);
      spine.state.update(0.016);
      spine.alpha = 0.001;

      requestAnimationFrame(() => {
        if (this.isAnimating) return;
        if (spine?.state) spine.state.clearTracks();
        if (spine) spine.alpha = 0.001;
      });
    } catch {
      // Silently ignore warmup errors
    }
  }

  // ── Get Spine for Symbol ────────────────────────────────────────────

  /**
   * Get the correct spine instance for a given symbolId.
   * Also applies the skin if the symbol has one (e.g. jackpots: j1, j2, j3).
   */
  _getSpineForSymbol(symbolId) {
    const info = symbolData[symbolId];
    if (!info?.spine) return null;

    const spine = this.prePositionedSpines.get(info.spine);
    if (!spine) return null;

    // Apply skin if defined (jackpots, low-pay coins with different skins)
    if (info.skin && spine.skeleton?.data) {
      const skin = spine.skeleton.data.findSkin(info.skin);
      if (skin) {
        spine.skeleton.setSkin(skin);
        spine.skeleton.setSlotsToSetupPose();
      }
    } else if (info.name && spine.skeleton?.data) {
      // Try symbol name as skin (coins: c1, c2, ..., c15)
      const skin = spine.skeleton.data.findSkin(info.name);
      if (skin) {
        spine.skeleton.setSkin(skin);
        spine.skeleton.setSlotsToSetupPose();
      }
    }

    return spine;
  }

  // ── Coin Display Value ──────────────────────────────────────────────

  _getCoinDisplayValue(symbolId) {
    const symbolValue = symbolData[symbolId]?.value;
    if (!symbolValue) return "";

    try {
      const state = store.getState();
      const freeRoundsActive = state?.freeRounds?.status === "active";
      const freeRoundsBet = state?.freeRounds?.betValue || null;
      const regularBet = state?.game?.bet || 1;
      const bet =
        freeRoundsActive && freeRoundsBet ? freeRoundsBet : regularBet;

      const cacheKey = `${symbolId}_${symbolValue}_${bet}`;
      if (this._coinDisplayValueCache.has(cacheKey)) {
        return this._coinDisplayValueCache.get(cacheKey);
      }

      const coinValue = parseInt(symbolValue, 10);
      if (isNaN(coinValue) || coinValue <= 0) return "";

      const formatted = formatToCustomLocale(coinValue * bet);
      this._coinDisplayValueCache.set(cacheKey, formatted);

      return formatted;
    } catch {
      return "";
    }
  }

  // ── Coin Value Text ─────────────────────────────────────────────────

  createCoinValueText(displayValue) {
    if (!displayValue) return null;

    const counterContainer = new PIXI.Container();
    counterContainer.name = "coin_value_text";
    counterContainer.alpha = 1;
    counterContainer.zIndex = 1000;

    const fontSize =
      this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;

    const bitmapText = new PIXI.BitmapText(displayValue, {
      fontName: "font_secondary_scaled",
      fontSize,
    });
    bitmapText.anchor.set(0.5);
    bitmapText.roundPixels = true;

    bitmapText.updateText();

    // Auto-fit width (same as OverlayReelController)
    const maxTextWidth = this.symbolWidth * (1 - 0.12 * 2);
    if (bitmapText.width > maxTextWidth) {
      const scale = maxTextWidth / bitmapText.width;
      bitmapText.scale.set(scale);
    }

    counterContainer.addChild(bitmapText);
    return { counterContainer, bitmapText };
  }

  /**
   * Recalculate auto-fit scaling on the coin value bitmap text.
   * Resets scale to 1, measures actual width, and shrinks uniformly
   * if the text exceeds the available width (symbolWidth minus padding).
   *
   * Called after every counter text update and on resize.
   */
  autoFitCoinValueText() {
    if (!this.coinValueBitmapText || this.coinValueBitmapText.destroyed) return;

    this.coinValueBitmapText.scale.set(1);
    this.coinValueBitmapText.updateText();

    // Compute totalScale to convert maxTextWidth to spine-local units
    // (text fontSize is in spine-local space after showCoinValueText adjustment)
    let totalScale = 1;
    if (this._coinValueSpine) {
      const spineScale = this._coinValueSpine.scale.x || 1;
      const valueBone = this._coinValueSpine.skeleton?.findBone("value");
      const boneBaseScale = Math.abs(valueBone?.a) || 1;
      totalScale = spineScale * boneBaseScale;
    }

    const paddingPercent = 0.12;
    const maxTextWidth = totalScale !== 0
      ? (this.symbolWidth * (1 - paddingPercent * 2)) / totalScale
      : this.symbolWidth * (1 - paddingPercent * 2);
    const actualTextWidth = this.coinValueBitmapText.width;

    if (actualTextWidth > maxTextWidth) {
      const scale = maxTextWidth / actualTextWidth;
      this.coinValueBitmapText.scale.set(scale);
    }
  }

  showCoinValueText(symbolId, spineInstance) {
    this.hideCoinValueText();

    const displayValue = this._getCoinDisplayValue(symbolId);
    if (!displayValue) return;

    const result = this.createCoinValueText(displayValue);
    if (!result) return;

    this.coinValueContainer = result.counterContainer;
    this.coinValueBitmapText = result.bitmapText;
    this._coinValueSpine = spineInstance; // Store for removeSlotObject on cleanup

    // Attach to the animated txt_slot_value slot.
    // Two scale factors shrink the text:
    //   1. spine.scale.x (PIXI scale to fit symbol in grid)
    //   2. bone.a (bone's world transform scale)
    // Counter both by scaling fontSize up by 1/(spineScale * boneBaseScale).
    if (spineInstance) {
      const spineScale = spineInstance.scale.x || 1;

      // Force world transform update so bone values are computed
      spineInstance.update(0);
      const valueBone = spineInstance.skeleton?.findBone("value");
      const boneBaseScale = Math.abs(valueBone?.a) || 1;

      const totalScale = spineScale * boneBaseScale;
      if (totalScale && totalScale !== 0) {
        this.coinValueBitmapText.fontSize = this.coinValueBitmapText.fontSize / totalScale;
        this.coinValueBitmapText.scale.set(1);
        this.coinValueBitmapText.updateText();

        // Re-check auto-fit in spine-local units
        const maxTextWidth = (this.symbolWidth * (1 - 0.12 * 2)) / totalScale;
        if (this.coinValueBitmapText.width > maxTextWidth) {
          this.coinValueBitmapText.scale.set(maxTextWidth / this.coinValueBitmapText.width);
        }
      }

      spineInstance.addSlotObject("txt_slot_value", this.coinValueContainer);
    }
  }

  hideCoinValueText() {
    // Detach from spine slot BEFORE destroying (prevents localTransform error)
    if (this._coinValueSpine && this.coinValueContainer && !this.coinValueContainer.destroyed) {
      try {
        this._coinValueSpine.removeSlotObject("txt_slot_value", this.coinValueContainer);
      } catch {
        // Silent — spine may already be destroyed
      }
    }

    if (this.coinValueContainer) {
      if (this.coinValueContainer.parent) {
        this.coinValueContainer.parent.removeChild(this.coinValueContainer);
      }
      this.coinValueContainer.destroy({ children: true });
      this.coinValueContainer = null;
      this.coinValueBitmapText = null;
    }
    this._coinValueSpine = null;
  }

  // ── Hide Active Spine ───────────────────────────────────────────────

  _hideActiveSpine() {
    if (!this.activeSpineKey) return;

    const spine = this.prePositionedSpines.get(this.activeSpineKey);
    if (spine) {
      spine.alpha = 0.001;
      spine.autoUpdate = false;
      if (spine.state) spine.state.clearTracks();
      if (spine.skeleton?.setToSetupPose) spine.skeleton.setToSetupPose();
    }

    this.activeSpineKey = null;
    this.isAnimating = false;
    this.currentAnimationType = null;
    this.currentSymbolId = null;
    this.hideCoinValueText();
  }

  // ── Public Animation Methods ────────────────────────────────────────

  /**
   * Show idle animation for any bonus symbol (coin, jackpot, or royal).
   */
  showIdleAnimation(symbolId) {
    if (!this.overlaySymbolContainer || !isBonusSymbol(symbolId)) return;

    // Hide previous if different
    if (this.activeSpineKey) this._hideActiveSpine();

    const spine = this._getSpineForSymbol(symbolId);
    if (!spine) return;

    const info = symbolData[symbolId];
    const spineName = info.spine;

    spine.alpha = 1;
    spine.autoUpdate = true;
    this.isAnimating = true;
    this.activeSpineKey = spineName;
    this.currentAnimationType = "idle";
    this.currentSymbolId = symbolId;

    const idleAnim = this._selectAnimation(spineName, "idle");
    if (spine.state && idleAnim) {
      spine.state.clearTracks();
      spine.state.addAnimation(0, idleAnim, true, 0);
    }

    // Show coin value text for coin symbols only (jackpots display tier via spine skins)
    if (info.value && !isJackpotSymbol(symbolId)) {
      this.showCoinValueText(symbolId, spine);
    }
  }

  /**
   * Show landing → win animation.
   * After landing, coins play their win animation (looping) until collected.
   */
  showLandingAnimation(symbolId) {
    if (!this.overlaySymbolContainer || !isBonusSymbol(symbolId)) return;

    if (this.activeSpineKey) this._hideActiveSpine();

    const spine = this._getSpineForSymbol(symbolId);
    if (!spine) return;

    const info = symbolData[symbolId];
    const spineName = info.spine;

    spine.alpha = 1;
    spine.autoUpdate = true;
    this.isAnimating = true;
    this.activeSpineKey = spineName;
    this.currentAnimationType = "landing";
    this.currentSymbolId = symbolId;

    const landAnim = this._selectAnimation(spineName, "land");
    const idleAnim = this._selectAnimation(spineName, "idle");

    if (spine.state) {
      spine.state.clearTracks();
      if (landAnim && idleAnim) {
        spine.state.addAnimation(0, landAnim, false, 0);
        spine.state.addAnimation(0, idleAnim, true, 0);
      } else if (idleAnim) {
        spine.state.addAnimation(0, idleAnim, true, 0);
      } else if (landAnim) {
        spine.state.addAnimation(0, landAnim, false, 0);
      }
    }

    if (info.value && !isJackpotSymbol(symbolId)) {
      this.showCoinValueText(symbolId, spine);
    }
  }

  /**
   * Show win animation.
   */
  showWinAnimation(symbolId, loop = true, showText = true) {
    if (!this.overlaySymbolContainer || !isBonusSymbol(symbolId)) return;

    if (this.activeSpineKey) this._hideActiveSpine();

    const spine = this._getSpineForSymbol(symbolId);
    if (!spine) return;

    const info = symbolData[symbolId];
    const spineName = info.spine;

    spine.alpha = 1;
    spine.autoUpdate = true;
    this.isAnimating = true;
    this.activeSpineKey = spineName;
    this.currentAnimationType = "win";
    this.currentSymbolId = symbolId;

    const winAnim = this._selectAnimation(spineName, "win");
    if (spine.state && winAnim) {
      spine.state.clearTracks();
      spine.state.addAnimation(0, winAnim, loop, 0);

      // Queue idle after non-looping win so spine doesn't freeze
      if (!loop) {
        const idleAnim = this._selectAnimation(spineName, "idle");
        if (idleAnim) {
          spine.state.addAnimation(0, idleAnim, true, 0);
        }
      }
    }

    if (showText && info.value && !isJackpotSymbol(symbolId)) {
      this.showCoinValueText(symbolId, spine);
    }
  }

  /**
   * Show collect animation (played when a coin arrives at a royal symbol).
   * Non-looping — queues idle after completion.
   */
  showCollectAnimation(symbolId, showText = true) {
    if (!this.overlaySymbolContainer || !isBonusSymbol(symbolId)) return;

    if (this.activeSpineKey) this._hideActiveSpine();

    const spine = this._getSpineForSymbol(symbolId);
    if (!spine) return;

    const info = symbolData[symbolId];
    const spineName = info.spine;

    spine.alpha = 1;
    spine.autoUpdate = true;
    this.isAnimating = true;
    this.activeSpineKey = spineName;
    this.currentAnimationType = "collect";
    this.currentSymbolId = symbolId;

    const collectAnim = this._selectAnimation(spineName, "collect");
    if (spine.state && collectAnim) {
      spine.state.clearTracks();
      spine.state.addAnimation(0, collectAnim, false, 0);

      // Queue idle after collect finishes so spine doesn't freeze
      const idleAnim = this._selectAnimation(spineName, "idle");
      if (idleAnim) {
        spine.state.addAnimation(0, idleAnim, true, 0);
      }
    }

    if (showText && info.value && !isJackpotSymbol(symbolId)) {
      this.showCoinValueText(symbolId, spine);
    }
  }

  /**
   * Hide all animations.
   */
  hideAnimation() {
    this._hideActiveSpine();
  }

  // ── Resize ──────────────────────────────────────────────────────────

  resizeAnimations(symbolWidth, symbolHeight) {
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this._spineScaleCache.clear();

    const scaleData = this._calculateSpineScale(symbolWidth, symbolHeight);

    for (const spine of this.prePositionedSpines.values()) {
      spine.position.set(scaleData.x, scaleData.y);
      spine.scale.set(scaleData.scale);
    }

    // Update coin value text fontSize on resize.
    // Must apply same totalScale counter (spine PIXI scale × bone world scale)
    // as showCoinValueText, since both factors change on resize.
    if (this.coinValueBitmapText && !this.coinValueBitmapText.destroyed) {
      let totalScale = 1;
      if (this._coinValueSpine) {
        const spineScale = this._coinValueSpine.scale.x || 1;
        this._coinValueSpine.update(0);
        const valueBone = this._coinValueSpine.skeleton?.findBone("value");
        const boneBaseScale = Math.abs(valueBone?.a) || 1;
        totalScale = spineScale * boneBaseScale;
      }

      const baseFontSize =
        this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
      this.coinValueBitmapText.fontSize = totalScale !== 0 ? baseFontSize / totalScale : baseFontSize;
      this.coinValueBitmapText.scale.set(1);
      this.coinValueBitmapText.updateText();

      // Re-check auto-fit in spine-local units
      const maxTextWidth = totalScale !== 0
        ? (this.symbolWidth * (1 - 0.12 * 2)) / totalScale
        : this.symbolWidth * (1 - 0.12 * 2);
      if (this.coinValueBitmapText.width > maxTextWidth) {
        this.coinValueBitmapText.scale.set(maxTextWidth / this.coinValueBitmapText.width);
      }
    }
  }

  // ── Cleanup ─────────────────────────────────────────────────────────

  cleanup() {
    this.hideCoinValueText();

    for (const spine of this.prePositionedSpines.values()) {
      if (spine.state) spine.state.clearTracks();
      if (spine.parent) spine.parent.removeChild(spine);
      spine.destroy();
    }
    this.prePositionedSpines.clear();

    this.animationCaches.clear();
    this.animationAvailability.clear();
    this._spineScaleCache.clear();
    this._coinDisplayValueCache.clear();
  }
}

export default RoyalCollectOverlaySymbolController;
